<template>
  <div>
    <p class="font--p-3">운전면허증 인증</p>
    <v-row class="file-upload" no-gutters>
      <v-col cols="12">
        <SkeletonIdLoader :is-active="!!idImage" />
      </v-col>
      <v-col cols="12">
        <p v-show="!!idImage" class="mt-3 mb-1 error--text font-size--16">
          ※ 개인정보 보호를 위해 미리보기가 제공되지 않습니다
        </p>
      </v-col>
      <v-col v-if="idImage" cols="12" class="d-flex justify-center my-3 font-size--15">
        <label class="mr-2 font-weight-bold" for="id-card"> 파일명: </label>

        <input readonly id="id-card" type="text" :value="idImage.name" />
      </v-col>

      <v-col cols="12" class="file-upload__btn" :class="{ float: idImage === null }">
        <FileUploadButton
          :files.sync="idImage"
          :isLoading="uploadFileLoading"
          @update:files="uploadFile"
          singleFile
        >
          {{ idImage ? '다른파일 업로드' : '운전면허증 업로드' }}
        </FileUploadButton>
      </v-col>
    </v-row>

    <v-row class="mt-3" no-gutters>
      <v-col cols="12" class="mb-4">
        <label class="font-size--16">이름</label>
        <v-text-field
          v-model="idForm.name"
          :rules="rules.name"
          :loading="isLoading"
          :error="!!invalidMsg.name"
          hide-details
        ></v-text-field>
        <ErrorText class="mt-1" :text="invalidMsg.name" />
      </v-col>

      <v-col cols="12" class="mb-4">
        <label class="font-size--16">주민등록번호</label>
        <v-row no-gutters>
          <v-col cols="4">
            <v-text-field
              v-input-restrict.number
              v-model="idForm.jumin1"
              :rules="rules.jumin1"
              :error="!!invalidMsg.jumin1"
              maxlength="6"
              hide-details
              type="tel"
              ref="ref-form-jumin1"
              @input="focusNextForm('jumin1')"
            ></v-text-field>
            <ErrorText class="mt-1" :text="invalidMsg.jumin1" />
          </v-col>

          <v-col cols="1" class="d-flex align-center justify-center">
            <p class="ma-0">ㅡ</p>
          </v-col>

          <v-col cols="7">
            <v-text-field
              v-input-restrict.number
              v-model="idForm.jumin2"
              :append-icon="jumin2Show ? 'mdi-eye' : 'mdi-eye-off'"
              :type="jumin2Show ? 'tel' : 'password'"
              :rules="rules.jumin2"
              :error="!!invalidMsg.jumin2"
              hide-details
              maxlength="7"
              inputmode="numeric"
              ref="ref-form-jumin2"
              @input="focusNextForm('jumin2')"
              @click:append="jumin2Show = !jumin2Show"
            ></v-text-field>
            <ErrorText class="mt-1" :text="invalidMsg.jumin2" />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" class="mb-4">
        <label class="font-size--16">면허번호</label>
        <v-row class="serial-form" no-gutters>
          <v-col
            v-for="(sf, index) in serialForm"
            :key="sf.name"
            :cols="sf.length"
            class="serial-area"
            :class="{ active: sf.name === focusingFieldName }"
          >
            <v-row no-gutters>
              <v-col cols="12">
                <p>
                  {{ sf.text }}
                </p>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12">
                <v-subheader
                  class="example-serial"
                  :style="{ letterSpacing: sf.length - 2 + 'px' }"
                >
                  {{ focusingFieldText(sf) }}
                </v-subheader>
                <v-subheader
                  v-if="index < serialForm.length - 1"
                  class="example-serial serial-hyphen"
                >
                  -
                </v-subheader>
              </v-col>
            </v-row>

            <v-row no-gutters>
              <v-col cols="12">
                <v-text-field
                  v-model="idForm[sf.name]"
                  hide-details
                  :rules="rules[sf.name]"
                  :maxlength="sf.length"
                  :name="sf.name"
                  :ref="`ref-form-${sf.name}`"
                  @input="focusNextForm(sf.name)"
                  @blur="focusingFieldName = null"
                  @focus="focusField($event)"
                />
                <v-subheader
                  v-if="index < serialForm.length - 1"
                  class="example-serial serial-hyphen"
                >
                  -
                </v-subheader>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" class="mb-8">
        <div class="label">
          <span>식별번호</span>
          <v-btn
            icon
            width="18px"
            height="18px"
            class="ml-1"
            @click="showSerialImg = !showSerialImg"
          >
            <v-icon color="grey lighten-1" class="font-size--18"> mdi-help-circle </v-icon>
          </v-btn>

          <div v-show="showSerialImg">
            <img
              src="@/assets/images/kyc/id-cards/serial_number.png"
              style="max-width: 330px; width: 100%"
            />
          </div>
        </div>
        <v-text-field
          v-model="idForm.passwordSerialNumber"
          :rules="rules.passwordSerialNumber"
          ref="ref-form-passwordSerialNumber"
          maxlength="6"
          placeholder="영문 및 숫자로 된 6자리"
          outlined
          single-line
          class="pwd-serial-number"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <BasicButton
          class="font-size--16"
          :disabled="saveLoading || !isValidAll"
          :loading="saveLoading"
          @click="save()"
        >
          인증하기
        </BasicButton>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Regex from '@/const/Regex'
import SkeletonIdLoader from '@/components/common/loading/SkeletonIdLoader.vue'
import FileUploadButton from '@/components/common/form/FileUploadButton.vue'
import { mapActions, mapState } from 'vuex'

export default {
  components: {
    SkeletonIdLoader,
    FileUploadButton,
  },
  data() {
    return {
      isLoading: false,
      saveLoading: false,
      uploadFileLoading: false,
      jumin2Show: false,
      idImage: null,
      rules: {
        name: [
          (value) => !!value || '이름을 입력해주세요.',
          (value) => Regex.NAME.REGEX.test(value) || Regex.NAME.MSG,
        ],
        jumin1: [
          (value) => !!value || '생년월일을 입력해주세요.',
          (value) => Regex.JUMIN1.REGEX.test(value) || Regex.JUMIN1.MSG,
        ],
        jumin2: [
          (value) => !!value || '주민등록번호를 입력해주세요.',
          (value) => Regex.JUMIN2.REGEX.test(value) || Regex.JUMIN2.MSG,
        ],
        areaNumber: [
          (value) => !!value || '면허정보를 입력해주세요',
          (value) => value?.length === 2 || '면허정보를 입력해주세요',
        ],
        yearNumber: [
          (value) => !!value || '면허정보를 입력해주세요',
          (value) => value?.length === 2 || '면허정보를 입력해주세요',
        ],
        serialNumber: [
          (value) => !!value || '면허정보를 입력해주세요',
          (value) => value?.length === 6 || '면허정보를 입력해주세요',
        ],
        endingNumber: [
          (value) => !!value || '면허정보를 입력해주세요',
          (value) => value?.length === 2 || '면허정보를 입력해주세요',
        ],
        passwordSerialNumber: [
          (value) => !!value || '암호일련번호를 입력해주세요.',
          (value) => value?.length === 6 || '암호일련번호가 올바르지 않습니다',
        ],
      },
      idForm: {
        name: null,
        jumin1: null,
        jumin2: null,
        areaNumber: null,
        yearNumber: null,
        serialNumber: null,
        endingNumber: null,
        passwordSerialNumber: null,
      },
      invalidMsg: {
        name: null,
        jumin1: null,
        jumin2: null,
        areaNumber: null,
        yearNumber: null,
        serialNumber: null,
        endingNumber: null,
        passwordSerialNumber: null,
      },
      serialForm: [
        {
          name: 'areaNumber',
          text: '지역',
          length: 2,
        },
        {
          name: 'yearNumber',
          text: '년도',
          length: 2,
        },
        {
          name: 'serialNumber',
          text: '일련번호',
          length: 6,
        },
        {
          name: 'endingNumber',
          text: '맨뒤',
          length: 2,
        },
      ],
      showSerialImg: true,
      focusingFieldName: null,
    }
  },
  computed: {
    ...mapState('auth', ['me']),
    formKeys() {
      return Object.keys(this.idForm)
    },
    driverLicense() {
      const result = { ...this.idForm, image: this.idImage }
      const { jumin1, jumin2 } = this.idForm
      const jumin = jumin1 + jumin2
      result['jumin'] = jumin
      result['birthday'] = null
      delete result.jumin1
      delete result.jumin2
      return result
    },
    updateUser() {
      if (!this.user) {
        return {}
      }
      const birthdate = this.user.birthdate
        ? this.user.birthdate.replace(/^(\d{0,4})(\d{0,2})(\d{0,2})$/g, '$1-$2-$3')
        : null
      return {
        ...this.user,
        birthdate,
      }
    },
    idFormArr() {
      const arr = ['jumin1', 'jumin2']
      for (const k of this.serialForm) {
        arr.push(k.name)
      }
      arr.push('passwordSerialNumber')
      return arr
    },
    isValidAll() {
      if (!this.idImage) {
        return false
      }

      return Object.keys(this.idForm).every((idKey) => {
        return !!this.idForm?.[idKey] && !this.invalidMsg?.[idKey]
      })
    },
  },
  watch: {
    'me': {
      deep: true,
      immediate: true,
      handler(v) {
        this.idForm.name = v.name
      },
    },
    'idForm.name': {
      immediate: true,
      handler() {
        this.validCheck('name')
      },
    },
  },
  methods: {
    ...mapActions('kyc', ['addDriverLicense']),
    ...mapActions('document', ['addDocuments']),
    ...mapActions('auth', ['getMe']),
    getFormErrMsg(type, newVal) {
      const rules = this.rules[type]
      let msg = null
      if (newVal !== null) {
        for (let index = 0; index < rules.length; index++) {
          const rule = rules[index]
          const isValid = rule(newVal)
          if (typeof isValid !== 'boolean') {
            msg = isValid
            break
          }
        }
      }
      return msg
    },
    validCheck(property) {
      const prop = this.idForm?.[property]
      const msg = this.getFormErrMsg(property, prop)
      this.invalidMsg[property] = msg
    },
    valueIsValid(property) {
      const value = this.idForm?.[property]
      const invalidMsg = this.invalidMsg?.[property]
      return !!value && !invalidMsg
    },
    focusNextForm(property) {
      this.validCheck(property)
      const isValid = this.valueIsValid(property)
      if (isValid) {
        const valueIdx = this.formKeys.indexOf(property)
        if (valueIdx > -1 && valueIdx < this.formKeys.length - 1) {
          const nextRefKey = this.formKeys[valueIdx + 1]
          let nextRef = this.$refs[`ref-form-${nextRefKey}`]
          if (Array.isArray(nextRef)) {
            nextRef = nextRef[0]
          }
          nextRef?.$refs?.input.focus()
        }
      }
    },
    focusField(e) {
      this.focusingFieldName = e.target.name
    },
    focusingFieldText(sf) {
      const txt = sf.name === this.focusingFieldName ? 'O' : 'X'
      const temp = []
      for (let index = 0; index < sf.length; index++) {
        temp.push(txt)
      }
      return temp.join('')
    },
    async uploadFile() {
      try {
        this.uploadFileLoading = true
        await this.addDocuments({
          key: 'identification',
          file: this.idImage,
        })
      } finally {
        this.uploadFileLoading = false
      }
    },
    async save() {
      if (!this.isValidAll) {
        return
      }

      const { isConfirmed } = await this.$swal.basic.confirm('운전면허증 인증을 신청합니다.')

      if (!isConfirmed) {
        return
      }

      try {
        this.saveLoading = true
        const { message, result } = await this.addDriverLicense(this.driverLicense)
        if (result === true) {
          this.$emit('success', { message, result })
        }
      } finally {
        this.saveLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.file-upload {
  position: relative;
  margin-bottom: 15px;
  text-align: center;

  .v-card {
    border-color: #1baaf1;
  }

  input {
    min-width: 200px;
    max-width: 280px;
  }

  &__btn {
    &.float {
      position: absolute;
      display: flex;
      height: 206px;
      align-items: center;
      justify-content: center;
    }
  }
}

.example-serial {
  color: #727d8c;
  font-size: 20px;
  font-weight: 900;
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
  white-space: nowrap;
}

.serial-hyphen {
  min-width: 20px;
}

.serial-form {
  .serial-area {
    padding-top: 10px !important;
    padding-bottom: 10px !important;

    &.active {
      p,
      .example-serial {
        color: #1baaf1;
      }
    }
  }

  p {
    font-size: 13px;
    line-height: 1.5;
    margin: 0;
    text-align: center;
    white-space: nowrap;
  }

  input {
    text-align: center;
  }

  .col {
    position: relative;
  }

  .v-input {
    width: 85%;
    margin: 0 auto;
    padding-top: 0;

    &::v-deep input {
      text-align: center;
    }
  }

  .serial-hyphen {
    position: absolute;
    top: 0;
    right: -10px;
  }
}

.pwd-serial-number {
  ::v-deep input {
    text-transform: uppercase;
  }
}
</style>
